import { useTranslation } from 'react-i18next';
import {
    HStack,
    VStack,
    Text,
    Button,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberDecrementStepper,
    NumberIncrementStepper,
    Select,
    Checkbox,
    Divider,
    SimpleGrid,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    useToast,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame } from '../manage/ManageDialogFrame';
import {
    usePrecastConfigStore,
    PrecastConfiguration,
    PRECAST_SHEET_SIZES,
    PRECAST_CAM_FORMATS,
} from '../../state/usePrecastConfigStore';

/**
 * Precast Configuration dialog (BIM: Precast > Configuration).
 * Central rule editor for every Precast automation: split/production
 * envelopes, reinforcement standards, mounting defaults and fabrication
 * sheet settings. Live-writes into the persisted config store; the Split,
 * Reinforcement and Fabrication tools read the same state.
 */
export function PrecastConfigurationDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'precastConfig');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const config = usePrecastConfigStore((state) => state.config);
    const update = usePrecastConfigStore((state) => state.updateConfig);
    const reset = usePrecastConfigStore((state) => state.resetConfig);
    const toast = useToast();

    const num = (label: string, value: number, min: number, max: number, step: number, unit: string, key: keyof PrecastConfiguration) => (
        <HStack justify="space-between" gap={3}>
            <Text fontSize="xs">{label} ({unit}):</Text>
            <NumberInput
                size="sm"
                w="120px"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(_, v) => Number.isFinite(v) && update({ [key]: v } as Partial<PrecastConfiguration>)}
            >
                <NumberInputField />
                <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                </NumberInputStepper>
            </NumberInput>
        </HStack>); return (<ManageDialogFrame
            title={t('misc.precastConfiguration')}
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            footer={
                <HStack justify="space-between" w="full">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            reset();
                            toast({ title: 'Precast standards reset to factory defaults', status: 'info', duration: 2500 });
                        }}
                        color="var(--color-text-secondary)"
                    >{t('misc.resetToDefaults')}</Button>
                    <Button size="sm" colorScheme="blue" onClick={onClose}>{t('misc.oK')}</Button>
                </HStack>
            }
        >
            <Tabs size="sm" isFitted colorScheme="blue">
                <TabList>
                    <Tab>{t('misc.split')}</Tab>
                    <Tab>{t('misc.reinforcement')}</Tab>
                    <Tab>{t('misc.connections')}</Tab>
                    <Tab>{t('misc.fabrication')}</Tab>
                </TabList>
                <TabPanels>
                    {/* ---- Split rules ---- */}
                    <TabPanel>
                        <SimpleGrid columns={2} spacingX={6} spacingY={3} minChildWidth="260px">
                            {num('Max panel width', config.maxPanelWidthMm, 500, 12000, 100, 'mm', 'maxPanelWidthMm')}
                            {num('Max panel height', config.maxPanelHeightMm, 500, 12000, 100, 'mm', 'maxPanelHeightMm')}
                            {num('Max panel weight', config.maxPanelWeightKg, 100, 50000, 100, 'kg', 'maxPanelWeightKg')}
                            {num('Minimum panel width', config.minPanelWidthMm, 100, 4000, 50, 'mm', 'minPanelWidthMm')}
                            <HStack justify="space-between" gap={3}>
                                <Text fontSize="xs">{t('misc.jointGap')}</Text>
                                <Select
                                    size="sm"
                                    w="120px"
                                    value={config.jointGapMm}
                                    onChange={(e) => update({ jointGapMm: Number(e.target.value) as PrecastConfiguration['jointGapMm'] })}
                                >
                                    <option value={10}>{t('misc.10Mm')}</option>
                                    <option value={15}>{t('misc.15Mm')}</option>
                                    <option value={20}>{t('misc.20Mm')}</option>
                                    <option value={25}>{t('misc.25Mm')}</option>
                                    <option value={30}>{t('misc.30Mm')}</option>
                                </Select>
                            </HStack>
                            {num('Edge gap (monolith trim)', config.edgeGapMm, 0, 500, 5, 'mm', 'edgeGapMm')}
                            <HStack justify="space-between" gap={3}>
                                <Text fontSize="xs">{t('misc.floorMode')}</Text>
                                <Select
                                    size="sm"
                                    w="160px"
                                    value={config.floorMode}
                                    onChange={(e) => update({ floorMode: e.target.value as PrecastConfiguration['floorMode'] })}
                                >
                                    <option value="HollowCore">{t('misc.hollowCore')}</option>
                                    <option value="SolidSlab">{t('misc.solidSlab')}</option>
                                </Select>
                            </HStack>
                            {config.floorMode === 'HollowCore'
                                && num('Hollow-core plank width', config.hollowCoreWidthMm, 400, 1800, 50, 'mm', 'hollowCoreWidthMm')}
                            <Divider />
                            <VStack align="start" spacing={1} gridColumn="span 2">
                                <Text fontSize="xs" fontWeight="bold">{t('misc.productionEnvelope')}</Text>
                                <Text fontSize="xs" color="text.secondary">
                                    Splitting an element never produces a panel wider than the max width, taller than the max height
                                    or heavier than the max weight at {config.concreteDensityKgM3} kg/m³. Adjacent panels keep a
                                    {` ${config.jointGapMm} mm `}joint.
                                </Text>
                            </VStack>
                        </SimpleGrid>
                    </TabPanel>

                    {/* ---- Reinforcement standards ---- */}
                    <TabPanel>
                        <SimpleGrid columns={2} spacingX={6} spacingY={3} minChildWidth="260px">
                            <HStack justify="space-between" gap={3}>
                                <Text fontSize="xs">{t('misc.style')}</Text>
                                <Select
                                    size="sm"
                                    w="180px"
                                    value={config.reinforcementStyle}
                                    onChange={(e) => update({ reinforcementStyle: e.target.value as PrecastConfiguration['reinforcementStyle'] })}
                                >
                                    <option value="Welded Wire Mesh">{t('misc.weldedWireMesh')}</option>
                                    <option value="Single Layer Bars">{t('misc.singleLayerBars')}</option>
                                    <option value="Double Layer Bars">{t('misc.doubleLayerBars')}</option>
                                </Select>
                            </HStack>
                            {num('Bar / wire diameter', config.rebarDiameterMm, 3, 32, 1, 'mm', 'rebarDiameterMm')}
                            {num('Bar spacing', config.rebarSpacingMm, 50, 500, 10, 'mm', 'rebarSpacingMm')}
                            {num('Clear cover', config.coverMm, 10, 75, 5, 'mm', 'coverMm')}
                            {num('Concrete density', config.concreteDensityKgM3, 1800, 2600, 50, 'kg/m³', 'concreteDensityKgM3')}
                        </SimpleGrid>
                    </TabPanel>

                    {/* ---- Connections / mounting defaults ---- */}
                    <TabPanel>
                        <SimpleGrid columns={2} spacingX={6} spacingY={3} minChildWidth="260px">
                            <HStack justify="space-between" gap={3}>
                                <Text fontSize="xs">{t('misc.liftingAnchorsWallPanel')}</Text>
                                <Select
                                    size="sm"
                                    w="120px"
                                    value={config.liftingAnchorsPerPanel}
                                    onChange={(e) => update({ liftingAnchorsPerPanel: Number(e.target.value) as 1 | 2 })}
                                >
                                    <option value={1}>1</option>
                                    <option value={2}>2</option>
                                </Select>
                            </HStack>
                            {num('Default plate / anchor size', config.defaultPlateSizeMm, 50, 500, 10, 'mm', 'defaultPlateSizeMm')}
                        </SimpleGrid>
                    </TabPanel>

                    {/* ---- Fabrication ---- */}
                    <TabPanel>
                        <SimpleGrid columns={2} spacingX={6} spacingY={3} minChildWidth="260px">
                            {num('Drawing scale (1:N)', config.shopDrawingScale, 1, 100, 5, '1:', 'shopDrawingScale')}
                            <HStack justify="space-between" gap={3}>
                                <Text fontSize="xs">{t('misc.sheetSize')}</Text>
                                <Select
                                    size="sm"
                                    w="220px"
                                    value={config.shopSheetSizeId}
                                    onChange={(e) => update({ shopSheetSizeId: e.target.value })}
                                >
                                    {PRECAST_SHEET_SIZES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                                </Select>
                            </HStack>
                            <HStack justify="space-between" gap={3}>
                                <Text fontSize="xs">{t('misc.cAMFormat')}</Text>
                                <Select
                                    size="sm"
                                    w="220px"
                                    value={config.camFormat}
                                    onChange={(e) => update({ camFormat: e.target.value as PrecastConfiguration['camFormat'] })}
                                >
                                    {PRECAST_CAM_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                                </Select>
                            </HStack>
                            <HStack justify="space-between" gap={3}>
                                <Text fontSize="xs">{t('misc.factory')}</Text>
                                <Select
                                    size="sm"
                                    w="220px"
                                    value={config.factoryName}
                                    onChange={(e) => update({ factoryName: e.target.value })}
                                >
                                    <option value="Yap Precast Plant">{t('misc.yapPrecastPlant')}</option>
                                    <option value="Elematic Line">{t('misc.elematicLine')}</option>
                                    <option value="Spechorn Formax">{t('misc.spechornFormax')}</option>
                                    <option value="Vollert VETEX">{t('misc.vollertVETEX')}</option>
                                </Select>
                            </HStack>
                            <VStack align="start" gridColumn="span 2">
                                <Checkbox
                                    size="sm"
                                    isChecked={config.showReinforcement}
                                    onChange={(e) => update({ showReinforcement: e.target.checked })}
                                >
                                    <Text fontSize="xs">{t('misc.showReinforcementOnShopDrawings')}</Text>
                                </Checkbox>
                                <Checkbox
                                    size="sm"
                                    isChecked={config.showMountingParts}
                                    onChange={(e) => update({ showMountingParts: e.target.checked })}
                                >
                                    <Text fontSize="xs">{t('misc.showEmbeddedPartsOnShopDrawings')}</Text>
                                </Checkbox>
                                <Checkbox
                                    size="sm"
                                    isChecked={config.showSchedule}
                                    onChange={(e) => update({ showSchedule: e.target.checked })}
                                >
                                    <Text fontSize="xs">{t('misc.showQuantityScheduleOnShopDrawings')}</Text>
                                </Checkbox>
                            </VStack>
                        </SimpleGrid>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </ManageDialogFrame>
    );
}
